import { Component, ElementRef, ViewChild, AfterViewInit, Input, Output, EventEmitter } from '@angular/core';
import ForceGraph3D from '3d-force-graph';
import * as THREE from 'three';
import { forceManyBody, forceLink } from 'd3-force-3d';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass';
import SpriteText from 'three-spritetext';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-technologies-view',
  templateUrl: './technologies-view.component.html',
  styleUrls: ['./technologies-view.component.scss'],
})
export class TechnologiesViewComponent implements AfterViewInit {
  @Output() statsChanged = new EventEmitter<{ totalProjects: number; mainConcepts: number; subConcepts: number }>();
  @ViewChild('graphContainer', { static: true }) graphEl!: ElementRef<HTMLDivElement>;
  graph: any;
  @Input() fullGraphData: { nodes: any[]; links: any[] } = { nodes: [], links: [] };
  rawData: { nodes: any[]; links: any[] } = { nodes: [], links: [] };

  currentLevel: number = 1;
  activeMainConcept: string | null = null;
  activeSubConcept: string | null = null;
  selectedProject: any = null;

  constructor(private route: ActivatedRoute) {}

  ngAfterViewInit() {
    // if backend enabled (this can be some dumb flag), then fetch from backend at
    // the ip 13.65.205.78:8000
    // if disabled, then fetch from local assets
    // get backendEnabled value from link query, for example /home/technologies-view?backendEnabled=true
    const backendEnabled: boolean = this.route.snapshot.queryParams['backendEnabled'] === 'true';
    if (backendEnabled) {
      fetch('http://13.65.205.78:8080/technologies-view')
        .then((res) => res.json())
        .then((data) => {
          console.log('Fetched data from backend:', data);

          this.rawData = data;
          this.initGraph();

          setTimeout(() => {
            const scene = this.graph.scene?.();
            if (scene) {
              this.addStarfieldBackground(scene);
            } else {
              console.warn('Scene still undefined');
            }
          }, 500);
        })
        .catch((error) => {
          console.error('Error fetching data from backend:', error);
        });
    } else {
      console.log('Fetching data from local assets');
      fetch('assets/technologies_view_data.json')
        .then((res) => res.json())
        .then((data) => {
          this.rawData = data;
          this.initGraph();

          setTimeout(() => {
            const scene = this.graph.scene?.();
            if (scene) {
              this.addStarfieldBackground(scene);
            } else {
              console.warn('Scene still undefined');
            }
          }, 500);
        });
    }
  }

  private emitStats(nodes: any[]) {
    const totalProjects = nodes.filter((n) => n.type === 'project').length;
    const mainConcepts = nodes.filter((n) => n.type === 'mainConcept').length;
    const subConcepts = nodes.filter((n) => n.type === 'subConcept').length;

    this.statsChanged.emit({ totalProjects, mainConcepts, subConcepts });
  }

  initGraph() {
    this.graph = new ForceGraph3D(this.graphEl.nativeElement)
      .width(this.graphEl.nativeElement.offsetWidth)
      .height(this.graphEl.nativeElement.offsetHeight)
      .graphData(this.filterLevel1Data())
      .nodeAutoColorBy('type')
      .nodeLabel((node: any) => (node.type === 'project' ? `${node.id || 'Untitled'}` : node.id))
      .backgroundColor('#000003')
      .d3Force('charge', forceManyBody().strength(-400))
      .d3Force('link', forceLink().distance(250))
      .nodeThreeObject((node) => this.createNodeObject(node))
      .onEngineTick(() => {
        const gData = this.graph.graphData();
        gData.nodes.forEach((n: any) => {
          n.z = 0;
          n.vz = 0;
        });
      })
      .onNodeClick(this.handleNodeClick.bind(this))
      .onEngineTick(() => {
        // Force projects to stay within its ring
        const gData = this.graph.graphData();

        // Find the active subConcept node if we're in level 3
        const subConceptNode =
          this.currentLevel === 3 ? gData.nodes.find((n: any) => n.id === this.activeSubConcept) : null;

        if (this.currentLevel === 3 && subConceptNode) {
          const centerX = subConceptNode.x;
          const centerY = subConceptNode.y;

          gData.nodes.forEach((node: any) => {
            if (node.type === 'project') {
              // Determine radius based on techMaturity
              const maturity = (node.techMaturity || 'none').toLowerCase();
              const techMaturityRings: Record<'adopt' | 'trial' | 'assess' | 'identified' | 'none', number> = {
                adopt: 50,
                trial: 90,
                assess: 130,
                identified: 170,
                none: 210,
              };

              const radius = techMaturityRings[maturity as keyof typeof techMaturityRings];

              // Compute current angle relative to the subConcept
              const dx = node.x - centerX;
              const dy = node.y - centerY;
              const angle = Math.atan2(dy, dx);

              // Lock the node to the ring
              node.x = centerX + radius * Math.cos(angle);
              node.y = centerY + radius * Math.sin(angle);
              node.z = 0; // keep it flat
            }
          });
        }
      });

    const controls = this.graph.controls();
    controls.enableRotate = false;
    controls.minPolarAngle = Math.PI / 2;
    controls.maxPolarAngle = Math.PI / 2;

    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(window.innerWidth, window.innerHeight),
      1.6, // strength
      1, //radius
      0,
    ); //threshold
    this.graph.postProcessingComposer().addPass(bloomPass);
  }

  filterLevel1Data() {
    this.currentLevel = 1;
    this.activeMainConcept = null;
    this.activeSubConcept = null;
    this.selectedProject = null;

    const nodes = this.rawData.nodes.filter((n) => n.id === 'Technology' || n.type === 'mainConcept');
    const links = this.rawData.links.filter((l) => {
      const sourceId = typeof l.source === 'string' ? l.source : l.source.id;
      return sourceId === 'Technology';
    });

    // Calculate subConcept count for each mainConcept
    const mainConcepts = nodes.filter((n) => n.type === 'mainConcept');
    const center = { x: 0, y: 0, z: 0 };

    const a = 80; // initial radius factor
    const b = 0.25; // spiral growth rate
    const total = mainConcepts.length;
    const angleStep = (2 * Math.PI) / total;

    mainConcepts.forEach((n, i) => {
      const subConceptLinks = this.rawData.links.filter((l) => {
        const sourceId = typeof l.source === 'string' ? l.source : l.source.id;
        const targetId = typeof l.target === 'string' ? l.target : l.target.id;
        return sourceId === n.id && this.rawData.nodes.some((nn) => nn.id === targetId && nn.type === 'subConcept');
      });

      const uniqueSubConcepts = Array.from(
        new Set(subConceptLinks.map((l) => (typeof l.target === 'string' ? l.target : l.target.id))),
      );

      n.subConceptCount = uniqueSubConcepts.length;
      n.subConceptIds = uniqueSubConcepts; // <-- store for satellite color mapping

      // Reverse index so node 0 is at largest theta (left side, farthest)
      const reversedIndex = total - 1 - i;

      // Angle offset to start spiral on positive X axis (right side)
      const angleOffset = 0;

      const theta = reversedIndex * angleStep + angleOffset;

      const offset = n.subConceptCount * 5;
      const r = a * Math.exp(b * theta) + offset;

      n.fx = center.x + r * Math.cos(theta);
      n.fy = center.y + r * Math.sin(theta);
      n.fz = 0;
    });

    const techNode = nodes.find((n) => n.id === 'Technology');
    if (techNode) {
      techNode.fx = center.x;
      techNode.fy = center.y;
      techNode.fz = center.z;
    }

    const result = { nodes, links };
    this.emitStats(result.nodes);
    return result;
  }

  filterLevel2Data(mainConceptId: string) {
    this.currentLevel = 2;
    this.activeMainConcept = mainConceptId;
    this.activeSubConcept = null;
    this.selectedProject = null;

    // Show mainConcept node and its subConcepts
    const nodes = this.rawData.nodes.filter(
      (n) =>
        n.id === mainConceptId ||
        (n.type === 'subConcept' &&
          this.rawData.links.some((l) => {
            const sourceId = typeof l.source === 'string' ? l.source : l.source.id;
            const targetId = typeof l.target === 'string' ? l.target : l.target.id;
            return sourceId === mainConceptId && targetId === n.id;
          })),
    );
    const links = this.rawData.links.filter((l) => {
      const sourceId = typeof l.source === 'string' ? l.source : l.source.id;
      return sourceId === mainConceptId;
    });

    // Position nodes: mainConcept in center, subConcepts around it
    const mainConceptNode = nodes.find((n) => n.id === mainConceptId);
    const subConcepts = nodes.filter((n) => n.type === 'subConcept');

    const center = { x: 0, y: 0, z: 0 };
    if (mainConceptNode) {
      mainConceptNode.fx = center.x;
      mainConceptNode.fy = center.y;
      mainConceptNode.fz = center.z;
    }

    const angleStep = (2 * Math.PI) / subConcepts.length;
    const ringSpacing = 500; // distance between subConcepts to avoid overlap of their rings

    subConcepts.forEach((sub, i) => {
      const angle = i * angleStep;
      sub.fx = center.x + ringSpacing * Math.cos(angle);
      sub.fy = center.y + ringSpacing * Math.sin(angle);
      sub.fz = 0;
    });

    const result = { nodes, links };
    this.emitStats(result.nodes);
    return result;
  }

  filterLevel3Data(subConceptId: string) {
    this.currentLevel = 3;
    this.activeSubConcept = subConceptId;
    this.selectedProject = null;

    // Type definition for tech maturity
    type TechMaturity = 'adopt' | 'trial' | 'assess' | 'identified' | 'none';

    // Show subConcept node and its projects
    const nodes = this.rawData.nodes.filter(
      (n) =>
        n.id === subConceptId ||
        (n.type === 'project' &&
          this.rawData.links.some((l) => {
            const sourceId = typeof l.source === 'string' ? l.source : l.source.id;
            const targetId = typeof l.target === 'string' ? l.target : l.target.id;
            return sourceId === subConceptId && targetId === n.id;
          })),
    );

    const links: any[] = [];

    const subConceptNode = nodes.find((n) => n.id === subConceptId);
    const projects = nodes.filter((n) => n.type === 'project');

    // Position the subConcept node at the center
    const center = { x: 0, y: 0, z: 0 };
    if (subConceptNode) {
      subConceptNode.fx = center.x;
      subConceptNode.fy = center.y;
      subConceptNode.fz = center.z;
    }

    // Define ring radius
    const techMaturityRings: Record<TechMaturity, number> = {
      adopt: 50,
      trial: 90,
      assess: 130,
      identified: 170,
      none: 210,
    };

    // Group projects by techMaturity
    const groupedProjects: Record<TechMaturity, any[]> = {
      adopt: [],
      trial: [],
      assess: [],
      identified: [],
      none: [],
    };

    projects.forEach((p) => {
      const maturity = (p.techMaturity || 'none').toLowerCase() as TechMaturity;
      if (groupedProjects[maturity]) {
        groupedProjects[maturity].push(p);
      } else {
        groupedProjects.none.push(p);
      }
    });

    // Position projects on the corresponding ring
    (Object.keys(groupedProjects) as TechMaturity[]).forEach((maturity) => {
      const projectList = groupedProjects[maturity];
      const radius = techMaturityRings[maturity];
      const angleStep = (2 * Math.PI) / Math.max(projectList.length, 1);

      projectList.forEach((p, i) => {
        const angle = i * angleStep;
        p.fx = center.x + radius * Math.cos(angle);
        p.fy = center.y + radius * Math.sin(angle);
        p.fz = 0;
      });
    });

    const result = { nodes, links };
    this.emitStats(result.nodes);
    return result;
  }

  createNodeObject(node: any) {
    const group = new THREE.Group();

    let color: number | string = 0x508ed7;
    if (node.type === 'mainConcept' || node.type === 'technology') color = 0xaddff7;
    else if (node.type === 'subConcept') color = 0xaddff7;

    if (node.type === 'technology' || node.type === 'mainConcept') {
      let radius = 20;
      if (node.type === 'technology') {
        radius = 30;
      }
      const tubeRadius = 1.5;
      const radialSegments = 16;
      const tubularSegments = 100;

      // Base torus (visible normally)
      const geometry = new THREE.TorusGeometry(radius, tubeRadius, radialSegments, tubularSegments);
      const material = new THREE.MeshBasicMaterial({ color: color });
      const torus = new THREE.Mesh(geometry, material);
      group.add(torus);

      const bloomGeometry = new THREE.TorusGeometry(radius, tubeRadius * 1.2, radialSegments, tubularSegments);
      const bloomMaterial = new THREE.MeshBasicMaterial({ color: 0x61cbff });
      const bloomTorus = new THREE.Mesh(bloomGeometry, bloomMaterial);
      bloomTorus.layers.set(1); // Add to bloom layer only
      group.add(bloomTorus);
    } else {
      // For project and subConcept, keep solid spheres
      const radius = node.type === 'project' ? 10 : 20;
      const sphere = new THREE.Mesh(new THREE.SphereGeometry(radius), new THREE.MeshBasicMaterial({ color }));
      group.add(sphere);
    }

    // Satellites for mainConcept (unchanged)
    if (node.type === 'mainConcept' && node.subConceptCount && this.currentLevel === 1) {
      const satelliteRadius = 35;
      for (let i = 0; i < node.subConceptCount; i++) {
        const angle = (2 * Math.PI * i) / node.subConceptCount;
        const x = satelliteRadius * Math.cos(angle);
        const y = satelliteRadius * Math.sin(angle);
        const geometry = new THREE.SphereGeometry(4, 16, 16);
        const material = new THREE.MeshBasicMaterial({ color: 0xaddff7 });
        const satellite = new THREE.Mesh(geometry, material);
        satellite.position.set(x, y, 0);
        group.add(satellite);
      }
    }

    // Rings for subConcept
    if (node.type === 'subConcept') {
      const labels = ['adopt', 'trial', 'assess', 'identified', 'none'];
      const ringMaterial = new THREE.MeshBasicMaterial({ color: 'gray', side: THREE.DoubleSide });
      labels.forEach((label, index) => {
        const ringRadius = 50 + index * 40;
        const ring = new THREE.Mesh(new THREE.RingGeometry(ringRadius - 1, ringRadius, 64), ringMaterial.clone());
        ring.rotation.z = Math.PI / 2;
        ring.position.y = -1.5 * index;
        group.add(ring);

        const ringLabel = new SpriteText(label);
        ringLabel.color = 'white';
        ringLabel.textHeight = 12;
        const angle = index * 0.5;
        const x = ringRadius * Math.cos(angle);
        const y = ringRadius * Math.sin(angle);
        ringLabel.position.set(x, y, 0);
        group.add(ringLabel);
      });
    }

    // Spiral for technology
    if (node.type === 'technology') {
      const texture = new THREE.TextureLoader().load('assets/spiral_tech.png');
      const material = new THREE.SpriteMaterial({ map: texture, transparent: true, opacity: 0.15 });
      const spiral = new THREE.Sprite(material);
      spiral.scale.set(165, 165, 1);
      spiral.position.set(0, 0, 0);
      group.add(spiral);
    }

    // Labels
    const label = new SpriteText(node.id);
    label.color = 'white';
    if (node.type === 'project') {
      label.textHeight = 10;
      label.position.set(0, 15, 0);
    } else if (node.type === 'technology') {
      label.textHeight = 30;
      label.position.set(0, 45, 0);
    } else {
      label.textHeight = 20;
      label.position.set(0, 30, 0);
    }
    group.add(label);

    return group;
  }

  handleNodeClick(node: any) {
    if (this.currentLevel === 1 && node.type === 'mainConcept') {
      this.graph.graphData(this.filterLevel2Data(node.id));
    } else if (this.currentLevel === 2 && node.type === 'subConcept') {
      this.graph.graphData(this.filterLevel3Data(node.id));
    } else if (this.currentLevel === 3 && node.type === 'project') {
      this.selectedProject = {
        id: node.id,
        domain: node.domains ? 'Domain: ' + node.domains : 'Domain: Unknown',
        techMaturity: node.techMaturity?.length > 0 ? 'Tech Maturity: ' + node.techMaturity : 'Tech Maturity: Unknown',
        team: node.team?.length > 0 ? 'Team: ' + node.team : 'Team: Unknown',
        STICTeam: node.STIC_team?.length > 0 ? 'Team Members: ' + node.STIC_team : 'Team Members: Unknown',
        stakeholders: node.stakeholders?.length > 0 ? 'Stakeholders: ' + node.stakeholders : 'Stakeholders: Unknown',
        mainConcept:
          node.mainConcepts?.length > 0 ? 'Main Technologies: ' + node.mainConcepts : 'Main Technologies: Unknown',
        subConcept:
          node.subConcepts?.length > 0 ? 'Sub Technologies: ' + node.subConcepts : 'Sub Technologies: Unknown',
        description: node.markdown ? 'Description: ' + node.markdown : 'No description available.',
        path: node.path || 'Unknown Path',
      };
    } else if (node.id === 'Technology') {
      this.graph.graphData(this.filterLevel1Data());
      this.selectedProject = null;
    }
  }

  closeInfoBox() {
    this.selectedProject = null;
  }

  resetGraph() {
    this.graph.graphData(this.filterLevel1Data());
    this.selectedProject = null;
  }

  centerGraph() {
    this.graph.zoomToFit(400);
  }

  addStarfieldBackground(scene: THREE.Scene) {
    const starsGeometry = new THREE.BufferGeometry();
    const starCount = 15000;

    const positions = new Float32Array(starCount * 3);
    for (let i = 0; i < starCount * 3; i++) {
      positions[i] = (Math.random() - 0.5) * 5000;
    }

    starsGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const starsMaterial = new THREE.PointsMaterial({
      color: 0xffffff,
      size: 2,
      sizeAttenuation: true,
      transparent: true,
      opacity: 0.7,
    });

    const stars = new THREE.Points(starsGeometry, starsMaterial);
    scene.add(stars);
  }
}
